// Guides. "Verified" is not used here. An admin approves a carrier account.
// The software does not query the EA or NRW register.
// Environment Centre move-out page checked 29 October 2026:
// https://www.environmentcentre.org.uk/moveout

import { EA_CARRIER_REGISTER, NRW_CARRIER_REGISTER } from './site';

const link = (text, href) => ({ t: text, href });

const SWANSEA_BULKY = 'https://www.swansea.gov.uk/bulkywaste';
const SWANSEA_RECYCLING = 'https://www.swansea.gov.uk/recyclingcentres';
const SWANSEA_SCRAP = 'https://www.swansea.gov.uk/scrapmetaldealers';
const MOVEOUT = 'https://www.environmentcentre.org.uk/moveout';
const FREEGLE = 'https://www.freegle.org.uk/';
const FREECYCLE = 'https://www.freecycle.org/';

export const GUIDES = [
  {
    slug: 'check-a-waste-carrier-licence',
    path: '/guides/check-a-waste-carrier-licence',
    title: 'How to check a waste carrier licence (Wales and England) | The WasteGame',
    description:
      'A short check before you hand over your rubbish. How to search the NRW and Environment Agency registers, what the result means, and what to ask for.',
    h1: 'How to check a waste carrier licence',
    datePublished: '2026-10-29',
    blocks: [
      {
        type: 'p',
        text: 'Do this before you hand over household waste. It takes a few minutes. The WasteGame does not collect waste and holds no waste carrier licence. This page is the check you can do yourself.',
      },
      {
        type: 'h2',
        text: 'The steps',
      },
      {
        type: 'ul',
        items: [
          'Ask the carrier for their registration number and the name it is registered under.',
          [
            'Look them up on the ',
            link('Natural Resources Wales register', NRW_CARRIER_REGISTER),
            ' if they are based in Wales, or the ',
            link('Environment Agency register', EA_CARRIER_REGISTER),
            ' if they are based in England. The same style of registration number appears on both. There is no example number on this page.',
          ],
          'Check the name, that it is upper tier for carrying other people’s waste, and the expiry date.',
          'Ask where the waste is going, and get a waste transfer note or a receipt.',
          'If the waste is later fly-tipped, you can be fined. That is the householder’s duty of care.',
        ],
      },
      {
        type: 'h2',
        text: 'What The WasteGame actually does',
      },
      {
        type: 'p',
        text: 'A carrier types a waste carrier registration number when they create an account. They cannot send a price until an admin approves that account. On the admin screen the number is shown next to links to the Environment Agency register and the Natural Resources Wales register, so a person can look it up.',
      },
      {
        type: 'p',
        text: 'The site does not send the number to those registers and does not read a result back. It does not check the registered name, the tier or the expiry date for you. Approval means an admin allowed the account to send prices. It does not mean the software confirmed the registration. Do the register check yourself.',
      },
    ],
    faqs: [
      {
        q: 'Does The WasteGame confirm the number on the live register?',
        a: 'No. The software does not query the Environment Agency or Natural Resources Wales register. An admin approves the account before a carrier can send a price, and the admin screen links to those registers. The name, tier and expiry are still yours to check.',
      },
      {
        q: 'Is a registration a promise they will take my job?',
        a: 'No. Posting is free. It does not book a van, and it does not mean anyone will send a price. You pay by card only if you accept a price and the job is marked complete.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/guides/free-scrap-collection-swansea', label: 'Free scrap collection' },
      { href: '/swansea/house-clearance', label: 'House clearance in Swansea' },
    ],
  },
  {
    slug: 'rubbish-removal-cost-swansea',
    path: '/guides/rubbish-removal-cost-swansea',
    title: 'How much does rubbish removal cost in Swansea? | The WasteGame',
    description:
      'What affects the price of rubbish removal in Swansea, cheaper legal options to try first, and how to compare carriers’ own prices.',
    h1: 'How much does rubbish removal cost in Swansea?',
    datePublished: '2026-10-29',
    blocks: [
      {
        type: 'p',
        text: 'There is no price list on this page. A carrier prices the job they can see. Two houses in Swansea with the same sofa can be different prices because of the stairs, the parking, or the weight.',
      },
      {
        type: 'h2',
        text: 'What changes the price',
      },
      {
        type: 'ul',
        items: [
          'Volume: a few bags, or a van load.',
          'Weight: rubble and soil cost more to move than bagged clothes.',
          'Awkward items: sofas, fridges and mattresses.',
          'Access: stairs, rear lanes, and somewhere legal to stop.',
          'Distance: a terrace in SA1 is not the same journey as a lane outside town.',
        ],
      },
      {
        type: 'h2',
        text: 'Cheaper legal options to try first',
      },
      {
        type: 'p',
        text: [
          'Kerbside recycling and rubbish collections, if the waste is something the council already takes. Swansea’s ',
          link('recycling centres', SWANSEA_RECYCLING),
          ' if you can get it there. The council’s paid ',
          link('bulky waste collection', SWANSEA_BULKY),
          ' for items such as fridges, sofas and mattresses. The price is on that page. Check it, because it can change. This guide does not copy a figure from it.',
        ],
      },
      {
        type: 'p',
        text: [
          'For items in good condition, try ',
          link('Freegle', FREEGLE),
          ', ',
          link('Freecycle', FREECYCLE),
          ' (look for a local group), or a reuse charity. In June, the ',
          link('Environment Centre’s student move-out scheme', MOVEOUT),
          ' collects reusable goods from student houses in Brynmill.',
        ],
      },
      {
        type: 'h2',
        text: 'If you still want a collection',
      },
      {
        type: 'p',
        text: 'Post one job and compare the prices carriers send. Posting is free. It does not book a van, and it does not mean anyone will send a price. If you accept one, you pay that price by card after the job is marked complete. There is no cash on the day for a priced job, and no customer fee. The WasteGame does not collect the waste.',
      },
      {
        type: 'p',
        text: [
          'Scrap metal posted as scrap collection has no cost to the customer. A time is not promised. Read the ',
          link('free scrap guide', '/guides/free-scrap-collection-swansea'),
          ' before you post a fridge as scrap. A fridge is not scrap.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Why is there no price table?',
        a: 'A made-up table would not be your price. Carriers send their own prices for the job you describe. You might get one price, several, or none.',
      },
      {
        q: 'When do I pay?',
        a: 'On a priced job, by card, after the job is marked complete. You pay the price you accepted. Posting, reading a price, and saying no are free.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/swansea/house-clearance', label: 'House clearance in Swansea' },
      { href: '/swansea/sofa-removal', label: 'Sofa removal' },
      { href: '/swansea/mattress-removal', label: 'Mattress removal' },
      { href: '/swansea/fridge-freezer-removal', label: 'Fridge and freezer removal' },
      { href: '/guides/check-a-waste-carrier-licence', label: 'How to check a waste carrier registration' },
    ],
  },
  {
    slug: 'free-scrap-collection-swansea',
    path: '/guides/free-scrap-collection-swansea',
    title: 'Free scrap and old appliance collection in Swansea: how to do it legally | The WasteGame',
    description:
      'How free scrap collection works in Swansea, the council licence a door-to-door collector needs, and what never counts as scrap.',
    h1: 'Free scrap and old appliance collection in Swansea',
    datePublished: '2026-10-29',
    blocks: [
      {
        type: 'p',
        text: 'Free to post. A local scrap collector collects it, and there is no cost to the customer. Posting does not promise a price or a time. The WasteGame does not collect the scrap.',
      },
      {
        type: 'h2',
        text: 'What the law asks of a collector',
      },
      {
        type: 'p',
        text: [
          'Someone collecting scrap metal door to door needs a scrap metal collector’s licence from the council for that area, and a waste carrier registration. The licence should be displayed on the vehicle. Swansea Council publishes this on its ',
          link('scrap metal dealers page', SWANSEA_SCRAP),
          '. Cash for scrap metal is not allowed. Ask to see the licence yourself.',
        ],
      },
      {
        type: 'p',
        text: [
          'A waste carrier registration is a different record. You can search the ',
          link('Natural Resources Wales register', NRW_CARRIER_REGISTER),
          ' or the ',
          link('Environment Agency register', EA_CARRIER_REGISTER),
          '. How The WasteGame treats carrier accounts is on the ',
          link('registration guide', '/guides/check-a-waste-carrier-licence'),
          '.',
        ],
      },
      {
        type: 'h2',
        text: 'What never counts as scrap',
      },
      {
        type: 'ul',
        items: [
          'Fridges and freezers. They hold refrigerant gases. Post them as a fridge, not as scrap.',
          'Gas bottles.',
          'Batteries.',
          'Anything with oil or fuel in it.',
        ],
      },
      {
        type: 'h2',
        id: 'reuse',
        text: 'Reuse before you scrap it',
      },
      {
        type: 'p',
        text: [
          'If the item works or is in good condition, offer it on ',
          link('Freegle', FREEGLE),
          ' or ',
          link('Freecycle', FREECYCLE),
          ', or ask a furniture reuse charity. In June, the Environment Centre runs a student move-out collection in Brynmill: ',
          link('Help out when you move out', MOVEOUT),
          '.',
        ],
      },
      {
        type: 'h2',
        text: 'Posting scrap on The WasteGame',
      },
      {
        type: 'p',
        text: [
          'Post the items on the ',
          link('free scrap collection page', '/swansea/free-scrap-collection'),
          '. There is no cost to the customer. A collector chooses whether to take the job. You are not promised a time. If nobody comes, you have not been charged.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is scrap collection free?',
        a: 'Posting is free, and there is no cost to the customer. That does not promise a collector, a payment, or a time.',
      },
    ],
    links: [
      { href: '/swansea/free-scrap-collection', label: 'Post scrap for free collection' },
      { href: '/swansea/fridge-freezer-removal', label: 'Fridge and freezer removal' },
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/areas/gorseinon', label: 'Gorseinon and Gowerton' },
    ],
  },
];

export const GUIDES_INDEX = {
  path: '/guides',
  title: 'Guides | The WasteGame',
  description:
    'Plain guides for Swansea and South Wales: how to check a waste carrier registration, what changes a rubbish removal price, and how free scrap collection works.',
  h1: 'Guides',
  intro: [
    'Short pages on checks and prices. No carrier counts, and no made-up price list. The WasteGame connects you with carriers. It does not collect waste and holds no waste carrier licence.',
    'Posting is free. It does not book a van, and it does not mean anyone will send a price.',
  ],
  reasons: [
    'Carriers type a waste carrier registration number when they sign up. They can send a price only after an admin has approved the account. The admin screen links to the Environment Agency and Natural Resources Wales registers. The site does not query those registers.',
    'On a priced job you pay by card after the job is marked complete. There is no customer fee, and you are not asked to hand over cash on the day.',
    'You post once. If carriers want the work, their prices come to you. Nobody is promised a price.',
  ],
};

export function guideBySlug(slug) {
  return GUIDES.find((guide) => guide.slug === slug) || null;
}

export function guideLinks() {
  return [
    { href: GUIDES_INDEX.path, label: 'All guides' },
    ...GUIDES.map((guide) => ({ href: guide.path, label: guide.h1 })),
  ];
}
